import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["image", "controls", "saveBtn", "label"]
  static values = { url: String, x: { type: Number, default: 50 }, y: { type: Number, default: 50 } }

  connect() {
    this.dragging = false
    this.editing = false
    this.initialX = this.xValue
    this.initialY = this.yValue
    this.applyPosition()
  }

  toggle() {
    this.editing = !this.editing
    this.controlsTarget.classList.toggle("hidden", !this.editing)
    this.element.classList.toggle("cursor-grab", this.editing)
    this.element.classList.toggle("select-none", this.editing)

    // Disable pointer events on overlays during editing so drag passes through
    this.element.parentElement?.querySelectorAll(':scope > :not([data-controller="photo-reposition"])').forEach(el => {
      if (el !== this.element) el.style.pointerEvents = this.editing ? "none" : ""
    })
    // Also handle siblings inside the same parent
    for (const sibling of this.element.parentElement?.children || []) {
      if (sibling !== this.element && !sibling.hasAttribute('data-photo-reposition-target')) {
        sibling.style.pointerEvents = this.editing ? "none" : ""
      }
    }

    if (!this.editing) {
      this.xValue = this.initialX
      this.yValue = this.initialY
      this.applyPosition()
    }
  }

  onStart(event) {
    if (!this.editing) return
    // Don't intercept clicks on buttons
    if (event.target.closest('button') || event.target.closest('a')) return
    event.preventDefault()
    this.dragging = true
    this.imageTarget.classList.replace("cursor-grab", "cursor-grabbing")

    const point = event.touches ? event.touches[0] : event
    this.startClientX = point.clientX
    this.startClientY = point.clientY
    this.startX = this.xValue
    this.startY = this.yValue

    this.boundMove = this.onMove.bind(this)
    this.boundEnd = this.onEnd.bind(this)
    document.addEventListener("mousemove", this.boundMove)
    document.addEventListener("mouseup", this.boundEnd)
    document.addEventListener("touchmove", this.boundMove, { passive: false })
    document.addEventListener("touchend", this.boundEnd)
  }

  onMove(event) {
    if (!this.dragging) return
    event.preventDefault()

    const point = event.touches ? event.touches[0] : event
    const rect = this.imageTarget.getBoundingClientRect()

    // Inversé : drag droite = object-position-x diminue
    const deltaX = ((point.clientX - this.startClientX) / rect.width) * 100
    const deltaY = ((point.clientY - this.startClientY) / rect.height) * 100

    this.xValue = Math.round(Math.min(100, Math.max(0, this.startX - deltaX)))
    this.yValue = Math.round(Math.min(100, Math.max(0, this.startY - deltaY)))

    this.applyPosition()
    this.updateLabel()
  }

  onEnd() {
    if (!this.dragging) return
    this.dragging = false
    this.imageTarget.classList.replace("cursor-grabbing", "cursor-grab")

    document.removeEventListener("mousemove", this.boundMove)
    document.removeEventListener("mouseup", this.boundEnd)
    document.removeEventListener("touchmove", this.boundMove)
    document.removeEventListener("touchend", this.boundEnd)
  }

  save() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
        "Accept": "application/json"
      },
      body: JSON.stringify({
        photo_position_x: this.xValue,
        photo_position_y: this.yValue
      })
    }).then(response => {
      if (response.ok) {
        this.initialX = this.xValue
        this.initialY = this.yValue
        this.saveBtnTarget.textContent = "Enregistré !"
        this.saveBtnTarget.classList.add("bg-green-600")
        setTimeout(() => {
          this.saveBtnTarget.textContent = "Enregistrer"
          this.saveBtnTarget.classList.remove("bg-green-600")
          this.toggle()
        }, 1500)
      }
    })
  }

  cancel() {
    this.xValue = this.initialX
    this.yValue = this.initialY
    this.applyPosition()
    this.toggle()
  }

  applyPosition() {
    this.imageTarget.style.objectPosition = `${this.xValue}% ${this.yValue}%`
  }

  updateLabel() {
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = `${this.xValue}% / ${this.yValue}%`
    }
  }
}
